// ---------------------------------------------------------------------------
// PresetSelector -- Dropdown to pick a saved design system preset
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react'
import { fetchDesignSystemPresets, type DesignSystemPreset } from '../lib/fetch-presets'
import type { DesignSystemState } from '../lib/design-system-options'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@cogo/react-renderer'

export interface PresetSelectorProps {
  userId: string
  onSelect: (preset: DesignSystemState | null) => void
  selectedPresetId?: string | null
}

export function PresetSelector({ userId, onSelect, selectedPresetId }: PresetSelectorProps) {
  const [presets, setPresets] = useState<DesignSystemPreset[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<string>(selectedPresetId ?? '')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    fetchDesignSystemPresets(userId).then((result) => {
      if (!cancelled) {
        setPresets(result.presets)
        setLoading(false)
      }
    })
    return () => { cancelled = true }
  }, [userId])

  function handleChange(value: string) {
    setSelected(value)
    if (!value) {
      onSelect(null)
      return
    }
    const preset = presets.find((p) => p.id === value)
    onSelect(preset?.tokens ?? null)
  }

  if (loading) {
    return (
      <div className="space-y-2">
        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          Design System Preset
        </label>
        <p className="text-sm text-muted-foreground">Loading presets...</p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
        Design System Preset
      </label>
      <Select
        value={selected || '__default__'}
        onValueChange={(v) => handleChange(v === '__default__' || v === null ? '' : v)}
      >
        <SelectTrigger
          data-testid="design-system-preset-trigger"
          className="h-8 w-full rounded-md border border-border bg-background/80 px-2.5 text-xs font-medium text-foreground shadow-none hover:bg-muted focus-visible:border-border focus-visible:ring-1 focus-visible:ring-border/60 focus-visible:ring-offset-0"
        >
          <SelectValue placeholder="Default (No Design System)" />
        </SelectTrigger>
        <SelectContent className="rounded-md border border-border bg-popover p-1 shadow-lg">
          <SelectItem value="__default__" className="text-xs">
            Default (No Design System)
          </SelectItem>
          {presets.map((p) => (
            <SelectItem key={p.id} value={p.id} className="text-xs">
              {p.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
